import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useRef, useState } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';
import {
  MiniHook,
  MiniHookPosition,
  MiniHookSettings,
  defaultMiniHookSettings,
} from '@/store/videoFeatures/types/mini-hook';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { videoService } from '@/services/videoService';
import {
  Magnet,
  Upload,
  X,
  Plus,
  Trash2,
  ChevronDown,
  Loader2,
} from 'lucide-react';

interface MiniHookConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

const generateId = () => `mh_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const parseTime = (timeStr: string): number => {
  const clean = timeStr.replace(/[^0-9:]/g, '');
  const parts = clean.split(':').map(Number);
  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
    return Math.max(0, parts[0] * 60 + Math.min(59, parts[1]));
  }
  return 0;
};

const applyTimeMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const POSITIONS: { id: MiniHookPosition; label: string }[][] = [
  [
    { id: 'top-left', label: '↖' },
    { id: 'top-center', label: '↑' },
    { id: 'top-right', label: '↗' },
  ],
  [
    { id: 'middle-left', label: '←' },
    { id: 'center', label: '●' },
    { id: 'middle-right', label: '→' },
  ],
  [
    { id: 'bottom-left', label: '↙' },
    { id: 'bottom-center', label: '↓' },
    { id: 'bottom-right', label: '↘' },
  ],
];

const POSITION_LABELS: Record<MiniHookPosition, string> = {
  get 'top-left'() { return translateInterfaceText("Superior esquerdo"); },
  get 'top-center'() { return translateInterfaceText("Superior central"); },
  get 'top-right'() { return translateInterfaceText("Superior direito"); },
  get 'middle-left'() { return translateInterfaceText("Meio esquerdo"); },
  'center': 'Centro',
  get 'middle-right'() { return translateInterfaceText("Meio direito"); },
  get 'bottom-left'() { return translateInterfaceText("Inferior esquerdo"); },
  get 'bottom-center'() { return translateInterfaceText("Inferior central"); },
  get 'bottom-right'() { return translateInterfaceText("Inferior direito"); },
};

const createEmptyHook = (): MiniHook => {
  const now = new Date().toISOString();
  return {
    id: generateId(),
    name: 'mini-hook',
    fileUrl: '',
    fileType: 'image',
    fileName: '',
    startTimeSeconds: 0,
    endTimeSeconds: 30,
    opacity: 100,
    position: 'bottom-right',
    widthPercent: 25,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  };
};

interface HookCardProps {
  hook: MiniHook;
  index: number;
  expanded: boolean;
  videoId?: string;
  onToggle: () => void;
  onChange: (patch: Partial<MiniHook>) => void;
  onRemove: () => void;
}

const HookCard: React.FC<HookCardProps> = ({ hook, index, expanded, videoId, onToggle, onChange, onRemove }) => {
  useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [startStr, setStartStr] = useState(formatTime(hook.startTimeSeconds));
  const [endStr, setEndStr] = useState(formatTime(hook.endTimeSeconds));

  const processFile = async (file: File) => {
    if (file.size > 20 * 1024 * 1024) {
      toast.error(translateInterfaceText("Arquivo muito grande. Máximo: 20 MB"));
      return;
    }
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error(translateInterfaceText("Formato inválido. Use JPG, PNG, WEBP ou GIF."));
      return;
    }
    if (!videoId) {
      toast.error(translateInterfaceText("Salve o vídeo antes de adicionar imagens aos mini-hooks."));
      return;
    }

    setIsUploading(true);
    try {
      const { fileUrl } = await videoService.uploadMiniHookImage(videoId, hook.id, file);
      const type: 'image' | 'gif' = file.type === 'image/gif' ? 'gif' : 'image';
      onChange({ fileUrl, fileName: file.name, fileType: type });
    } catch (error: any) {
      console.error('Failed to upload mini-hook image:', error);
      toast.error(error?.response?.data?.error || translateInterfaceText("Falha ao subir a imagem. Tente novamente."));
    } finally {
      setIsUploading(false);
    }
  };

  const handleTimingBlur = () => {
    onChange({
      startTimeSeconds: parseTime(startStr),
      endTimeSeconds: parseTime(endStr),
    });
  };

  return (
    <div className="rounded-xl border border-[#E4E7EC] bg-white overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-3 p-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-3 min-w-0 text-left"
        >
          <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#F8F9FB] border border-[#E4E7EC] flex-shrink-0 flex items-center justify-center">
            {hook.fileUrl ? (
              <img src={hook.fileUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <Magnet className="w-4 h-4 text-[#9CA3AF]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#1A1A1A] truncate">
              {translateInterfaceText("Mini Hook")}{" "}{index + 1}
            </p>
            <p className="text-[11px] text-[#6B7280] truncate">
              {formatTime(hook.startTimeSeconds)} – {formatTime(hook.endTimeSeconds)} • {POSITION_LABELS[hook.position]}
            </p>
          </div>
          <ChevronDown
            className={cn(
              'w-4 h-4 text-[#9CA3AF] transition-transform flex-shrink-0',
              expanded && 'rotate-180'
            )}
          />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="h-8 w-8 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all flex-shrink-0"
          title={translateInterfaceText("Remover")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-[#EEF0F3] p-4 space-y-4 bg-[#FCFCFD]">
          {/* Upload */}
          <div className="space-y-2">
            <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Imagem ou GIF")}</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); }}
            />
            {!hook.fileUrl ? (
              <div
                onClick={() => !isUploading && fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); if (!isUploading) setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  if (isUploading) return;
                  const f = e.dataTransfer.files?.[0];
                  if (f) processFile(f);
                }}
                className={cn(
                  'border-2 border-dashed rounded-xl p-6 flex flex-col items-center gap-2 transition-all',
                  isUploading
                    ? 'cursor-not-allowed border-[#E4E7EC] bg-[#F8F9FB]'
                    : 'cursor-pointer',
                  !isUploading && (isDragging
                    ? 'border-[#1D4FFF] bg-[#EEF4FF]'
                    : 'border-[#E4E7EC] hover:border-[#1D4FFF]/40 hover:bg-[#F8F9FB]')
                )}
              >
                <div className="w-9 h-9 rounded-lg bg-[#EEF4FF] flex items-center justify-center">
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 text-[#1D4FFF] animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 text-[#1D4FFF]" />
                  )}
                </div>
                <div className="text-center">
                  {isUploading ? (
                    <p className="text-[12px] font-medium text-[#1A1A1A]">{translateInterfaceText("Subindo arquivo...")}</p>
                  ) : (
                    <>
                      <p className="text-[12px] font-medium text-[#1A1A1A]">{translateInterfaceText("Clique ou arraste o arquivo aqui")}</p>
                      <p className="text-[11px] text-[#9CA3AF] mt-0.5">{translateInterfaceText("JPG, PNG, WEBP e GIF • até 20 MB")}</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="relative flex items-center gap-3 p-2.5 rounded-xl border border-[#E4E7EC] bg-white">
                <button
                  className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                  onClick={() => onChange({ fileUrl: '', fileName: '' })}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-[#E4E7EC] flex-shrink-0">
                  <img src={hook.fileUrl} alt={translateInterfaceText("Preview")} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0 pr-6">
                  <p className="text-[13px] font-semibold text-[#1A1A1A] truncate">{hook.fileName || translateInterfaceText("Arquivo")}</p>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">
                    {hook.fileType === 'gif' ? translateInterfaceText("GIF animado") : translateInterfaceText("Imagem estática")}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Timing */}
          <div className="space-y-2">
            <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Temporização")}</Label>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <Label className="text-[10.5px] text-[#6B7280] mb-1 block">{translateInterfaceText("Aparecer em")}</Label>
                <Input
                  value={startStr}
                  onChange={(e) => setStartStr(applyTimeMask(e.target.value))}
                  onBlur={handleTimingBlur}
                  placeholder="00:00"
                  className="bg-white font-mono text-center text-[13px] border-[#E4E7EC] h-9 focus-visible:ring-2 focus-visible:ring-[#1D4FFF]/20 focus-visible:border-[#1D4FFF]"
                  maxLength={5}
                />
              </div>
              <div>
                <Label className="text-[10.5px] text-[#6B7280] mb-1 block">{translateInterfaceText("Desaparecer em")}</Label>
                <Input
                  value={endStr}
                  onChange={(e) => setEndStr(applyTimeMask(e.target.value))}
                  onBlur={handleTimingBlur}
                  placeholder="00:30"
                  className="bg-white font-mono text-center text-[13px] border-[#E4E7EC] h-9 focus-visible:ring-2 focus-visible:ring-[#1D4FFF]/20 focus-visible:border-[#1D4FFF]"
                  maxLength={5}
                />
              </div>
            </div>
          </div>

          {/* Position Grid */}
          <div className="space-y-2">
            <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Posição na tela")}</Label>
            <div className="flex flex-col gap-1.5 mt-1">
              {POSITIONS.map((row, ri) => (
                <div key={ri} className="grid grid-cols-3 gap-1.5">
                  {row.map((pos) => (
                    <button
                      key={pos.id}
                      onClick={() => onChange({ position: pos.id })}
                      className={cn(
                        'h-10 rounded-lg text-[16px] border transition-all font-medium',
                        hook.position === pos.id
                          ? 'bg-[#1D4FFF] border-[#1D4FFF] text-white shadow-sm'
                          : 'bg-white border-[#E4E7EC] text-[#6B7280] hover:border-[#1D4FFF]/40 hover:bg-[#EEF4FF]'
                      )}
                      title={POSITION_LABELS[pos.id]}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#6B7280] mt-1">{POSITION_LABELS[hook.position]}</p>
          </div>

          {/* Width */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Largura")}</Label>
              <span className="text-[12px] font-bold text-[#1D4FFF] bg-[#EEF4FF] px-2.5 py-0.5 rounded-lg border border-[#D8E3FF]">
                {hook.widthPercent}%
              </span>
            </div>
            <Slider
              value={[hook.widthPercent]}
              onValueChange={([v]) => onChange({ widthPercent: v })}
              min={10}
              max={100}
              step={1}
              className="mt-1"
            />
            <p className="text-[11px] text-[#9CA3AF]">{translateInterfaceText("Percentual da largura do player")}</p>
          </div>

          {/* Opacity */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Opacidade")}</Label>
              <span className="text-[12px] font-bold text-[#1D4FFF] bg-[#EEF4FF] px-2.5 py-0.5 rounded-lg border border-[#D8E3FF]">
                {hook.opacity}%
              </span>
            </div>
            <Slider
              value={[hook.opacity]}
              onValueChange={([v]) => onChange({ opacity: v })}
              min={10}
              max={100}
              step={1}
              className="mt-1"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const MiniHookConfig: React.FC<MiniHookConfigProps> = ({ features, setFeature }) => {
  useLanguage();
  const settings: MiniHookSettings = features.miniHooks ?? defaultMiniHookSettings;
  const hooks = settings.hooks ?? [];
  const [expandedId, setExpandedId] = useState<string | null>(hooks[0]?.id ?? null);

  const commit = (next: MiniHookSettings) => setFeature('miniHooks', next);

  const toggleEnabled = (val: boolean) => {
    commit({ ...settings, enabled: val });
  };

  const addHook = () => {
    const newHook = createEmptyHook();
    commit({ ...settings, hooks: [...hooks, newHook] });
    setExpandedId(newHook.id);
  };

  const updateHook = (id: string, patch: Partial<MiniHook>) => {
    const now = new Date().toISOString();
    commit({
      ...settings,
      hooks: hooks.map((h) => (h.id === id ? { ...h, ...patch, updatedAt: now } : h)),
    });
  };

  const removeHook = (id: string) => {
    commit({ ...settings, hooks: hooks.filter((h) => h.id !== id) });
    if (expandedId === id) setExpandedId(null);
  };

  const isEnabled = settings.enabled;

  return (
    <div className="space-y-4 py-4 px-6">
      {/* Header Card */}
      <div className="flex items-center gap-3 rounded-[12px] p-3.5 bg-white border border-[#E4E7EC] transition-all duration-200 hover:border-[#D0D5DD]">
        <div className="flex-shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center bg-[#EEF4FF] text-[#1D4FFF]">
          <Magnet className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Mini Hook")}</h4>
          <p className="text-[12px] text-[#6B7280] leading-snug mt-1 line-clamp-2">
            {translateInterfaceText("Exiba uma ou mais imagens/GIFs em momentos estratégicos do vídeo para reforçar a atenção.")}</p>
        </div>
        <Switch checked={isEnabled} onCheckedChange={toggleEnabled} />
      </div>

      {isEnabled && (
        <div className="space-y-3">
          {hooks.length === 0 && (
            <div className="rounded-xl border border-dashed border-[#E4E7EC] bg-white p-5 text-center">
              <p className="text-[12px] text-[#6B7280]">{translateInterfaceText("Nenhum Mini Hook adicionado ainda.")}</p>
            </div>
          )}

          {hooks.map((h, i) => (
            <HookCard
              key={h.id}
              hook={h}
              index={i}
              expanded={expandedId === h.id}
              videoId={features.videoId}
              onToggle={() => setExpandedId(expandedId === h.id ? null : h.id)}
              onChange={(patch) => updateHook(h.id, patch)}
              onRemove={() => removeHook(h.id)}
            />
          ))}

          <button
            type="button"
            onClick={addHook}
            className="w-full h-10 rounded-xl border border-dashed border-[#1D4FFF]/40 bg-[#EEF4FF]/40 text-[#1D4FFF] text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-[#EEF4FF] hover:border-[#1D4FFF] transition-all"
          >
            <Plus className="w-4 h-4" />
            {translateInterfaceText("Adicionar Mini Hook")}</button>
        </div>
      )}
    </div>
  );
};
